import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../App';

const QUICK_PROMPTS = [
  { icon: '🍽️', label: 'Idées repas', prompt: 'Propose-moi 3 idées de repas rapides et économiques pour ce soir, adaptés à une famille avec un enfant.' },
  { icon: '✅', label: 'Organisation', prompt: 'Quelles tâches ménagères prioritaires dois-je faire cette semaine pour maintenir une maison propre et organisée ?' },
  { icon: '💰', label: 'Budget courses', prompt: 'Donne-moi des conseils pour réduire ma facture de courses tout en mangeant sainement.' },
  { icon: '👶', label: 'Activités enfant', prompt: 'Propose 5 activités créatives à faire avec un enfant de 5-8 ans à la maison.' },
  { icon: '🐱', label: 'Chatons', prompt: 'Quelles sont les priorités de santé pour des chatons de 2 mois ? Vaccins, antiparasitaires, alimentation ?' },
  { icon: '📅', label: 'Planning semaine', prompt: 'Aide-moi à organiser une semaine équilibrée pour une famille de 3 personnes qui travaille.' },
];

export default function AI() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [context, setContext] = useState(null);
  const [loadingContext, setLoadingContext] = useState(true);
  const [hasApiKey, setHasApiKey] = useState(false);

  const loadContext = useCallback(async () => {
    setLoadingContext(true);
    try {
      const _td = new Date(); const today = `${_td.getFullYear()}-${String(_td.getMonth()+1).padStart(2,'0')}-${String(_td.getDate()).padStart(2,'0')}`;
      const [tasksRes, eventsRes, mealsRes] = await Promise.allSettled([
        api.get('/tasks'),
        api.get(`/events?start=${today}&end=${today}`),
        api.get(`/meals?start=${today}&end=${today}`),
      ]);
      const tasks = tasksRes.status === 'fulfilled' ? tasksRes.value.data : [];
      const events = eventsRes.status === 'fulfilled' ? eventsRes.value.data : [];
      const meals = mealsRes.status === 'fulfilled' ? mealsRes.value.data : [];
      setContext({ tasks, events, meals, today });

      // Vérifier si clé Anthropic dispo
      const aiRes = await api.post('/ai/suggest-meal', { ingredients: 'test', preferences: '' }).catch(() => null);
      setHasApiKey(!!aiRes);
    } catch (e) {}
    setLoadingContext(false);
  }, []);

  useEffect(() => { loadContext(); }, [loadContext]);

  const buildSystemContext = () => {
    if (!context) return '';
    const urgentTasks = context.tasks.filter(t => t.priority === 'high').slice(0, 3);
    const todayMeals = context.meals;
    const todayEvents = context.events;
    return `Contexte familial actuel (${context.today}) :
- Tâches urgentes : ${urgentTasks.length ? urgentTasks.map(t => t.title).join(', ') : 'aucune'}
- Repas du jour : ${todayMeals.length ? todayMeals.map(m => `${m.meal_type}: ${m.title}`).join(', ') : 'non planifiés'}
- Événements aujourd'hui : ${todayEvents.length ? todayEvents.map(e => e.title).join(', ') : 'aucun'}
Famille : Fred (adulte, admin), Cécile (adulte), Manon (enfant).
Équipement cuisine : four, plaque de cuisson, robot Silvercrest.`;
  };

  const sendMessage = async (text) => {
    const q = text || input.trim();
    if (!q) return;
    setInput('');
    const newMsg = { role: 'user', content: q };
    setMessages(prev => [...prev, newMsg]);
    setLoading(true);

    try {
      const { data } = await api.post('/ai/chat', {
        message: q,
        context: buildSystemContext(),
        history: messages.slice(-6)
      });
      setMessages(prev => [...prev, { role: 'assistant', content: data.reply }]);
    } catch (e) {
      const errMsg = e.response?.data?.error || e.message || 'Erreur';
      // Fallback sans API key
      const fallbackReply = getFallbackReply(q);
      setMessages(prev => [...prev, { role: 'assistant', content: fallbackReply }]);
    }
    setLoading(false);
  };

  const getFallbackReply = (q) => {
    const ql = q.toLowerCase();
    if (ql.includes('repas') || ql.includes('manger') || ql.includes('dîner') || ql.includes('déjeuner')) {
      return '**Idées repas rapides :**\n\n🍝 Pâtes bolognaise (30 min, plaque)\n🍗 Poulet rôti légumes (45 min, four)\n🥗 Salade composée thon-pâtes (15 min)\n🫕 Velouté de légumes (20 min, robot Silvercrest)\n\n*Pour une génération automatique personnalisée, allez dans Repas → Générer la semaine.*';
    }
    if (ql.includes('tâche') || ql.includes('ménage') || ql.includes('organisation')) {
      return '**Priorités ménagères hebdomadaires :**\n\n✅ Lundi : Lessive + rangement\n✅ Mercredi : Aspiration + sols\n✅ Vendredi : Poubelles + salle de bain\n✅ Weekend : Courses + préparation repas semaine\n\n*Créez des tâches récurrentes dans l\'onglet Tâches pour automatiser ces rappels.*';
    }
    if (ql.includes('chat') || ql.includes('chaton') || ql.includes('vaccin') || ql.includes('vétérinaire')) {
      return '**Santé chatons 2 mois :**\n\n💉 **Primo-vaccination** : 2 mois (typhus, coryza, leucose)\n💉 **Rappel vaccin** : 3 mois\n🪱 **Vermifuge** : tous les mois jusqu\'à 6 mois\n🦟 **Antiparasitaires** : à partir de 2 mois\n✂️ **Stérilisation** : à partir de 5-6 mois recommandé\n\n*Les rappels vétérinaires ont été ajoutés dans votre agenda.*';
    }
    if (ql.includes('budget') || ql.includes('économ') || ql.includes('courses')) {
      return '**Conseils budget courses :**\n\n🛒 Faites une liste à l\'avance (onglet Courses)\n📅 Planifiez les repas de la semaine d\'abord\n🥚 Misez sur les protéines économiques : lentilles, œufs, sardines\n🥬 Achetez les légumes de saison\n❄️ Congelez les restes pour éviter le gaspillage\n\n*Utilisez "Générer la semaine" dans Repas pour des repas économiques automatiques.*';
    }
    return '**Assistant IA FamilyOS**\n\nPour activer l\'assistant IA complet (Claude), configurez la clé `ANTHROPIC_API_KEY` dans le fichier `.env` du serveur.\n\nEn attendant, je peux vous aider sur :\n- 🍽️ Idées de repas\n- ✅ Organisation & tâches\n- 💰 Budget courses\n- 🐱 Soins animaux\n- 👶 Activités enfants\n\nReformullez votre question sur l\'un de ces sujets !';
  };

  const clearChat = () => setMessages([]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <div className="topbar">
        <div className="page-title">✨ Assistant IA</div>
        {messages.length > 0 && (
          <button onClick={clearChat} className="btn btn-ghost btn-sm" style={{ fontSize: 12 }}>Effacer</button>
        )}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px', paddingBottom: 160 }}>

        {/* Contexte du jour */}
        {!loadingContext && context && (
          <div style={{ background: 'linear-gradient(135deg, #6C63FF, #9C88FF)', borderRadius: 16, padding: '16px 18px', marginBottom: 16, color: 'white' }}>
            <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 8, opacity: 0.9 }}>📋 Contexte du jour</div>
            <div style={{ fontSize: 12, lineHeight: 1.6 }}>
              {context.tasks.filter(t => t.priority === 'high').length > 0 && (
                <div>🔴 {context.tasks.filter(t => t.priority === 'high').length} tâche(s) urgente(s)</div>
              )}
              {context.meals.length > 0
                ? <div>🍽️ {context.meals.map(m => m.title).join(' · ')}</div>
                : <div style={{ opacity: 0.7 }}>🍽️ Repas non planifiés</div>}
              {context.events.length > 0 && <div>📅 {context.events.map(e => e.title).join(' · ')}</div>}
              {context.tasks.filter(t => t.priority === 'high').length === 0 && context.meals.length === 0 && context.events.length === 0 && (
                <div style={{ opacity: 0.7 }}>Rien de spécial aujourd'hui ✨</div>
              )}
            </div>
          </div>
        )}

        {/* Suggestions rapides */}
        {messages.length === 0 && (
          <>
            <div style={{ fontSize: 12, fontWeight: 800, color: '#8888AA', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
              Suggestions rapides
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 20 }}>
              {QUICK_PROMPTS.map((p, i) => (
                <button key={i} onClick={() => sendMessage(p.prompt)}
                  style={{ background: 'white', border: '2px solid #E0E4F0', borderRadius: 14, padding: '12px 14px', textAlign: 'left', cursor: 'pointer', transition: 'border-color 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = '#6C63FF'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = '#E0E4F0'}>
                  <div style={{ fontSize: 22, marginBottom: 4 }}>{p.icon}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#333355' }}>{p.label}</div>
                </button>
              ))}
            </div>

            {!hasApiKey && (
              <div style={{ background: '#FFF8E1', borderRadius: 12, padding: '12px 14px', fontSize: 12, color: '#E65100', lineHeight: 1.6 }}>
                <strong>ℹ️ Mode suggestions intégrées</strong><br />
                Pour activer l'IA complète (Claude), configurez <code style={{ background: '#fff', padding: '1px 4px', borderRadius: 4 }}>ANTHROPIC_API_KEY</code> dans le <code>.env</code> du serveur.
              </div>
            )}
          </>
        )}

        {/* Messages */}
        {messages.map((msg, i) => (
          <div key={i} style={{
            display: 'flex',
            justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
            marginBottom: 12
          }}>
            {msg.role === 'assistant' && (
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg, #6C63FF, #9C88FF)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0, marginRight: 8, alignSelf: 'flex-end' }}>✨</div>
            )}
            <div style={{
              maxWidth: '80%',
              padding: '12px 16px',
              borderRadius: msg.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
              background: msg.role === 'user' ? 'linear-gradient(135deg, #6C63FF, #8B80FF)' : 'white',
              color: msg.role === 'user' ? 'white' : '#333355',
              fontSize: 14,
              lineHeight: 1.6,
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              whiteSpace: 'pre-wrap'
            }}>
              {msg.content}
            </div>
            {msg.role === 'user' && (
              user.avatar_url
                ? <img src={user.avatar_url} alt={user.name} style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${user.color}`, flexShrink: 0, marginLeft: 8, alignSelf: 'flex-end' }} />
                : <div style={{ width: 32, height: 32, borderRadius: '50%', background: user.color + '33', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0, marginLeft: 8, alignSelf: 'flex-end' }}>{user.avatar_emoji}</div>
            )}
          </div>
        ))}

        {loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg, #6C63FF, #9C88FF)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>✨</div>
            <div style={{ background: 'white', borderRadius: '18px 18px 18px 4px', padding: '14px 18px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
              <span className="spinner" style={{ width: 16, height: 16 }} />
            </div>
          </div>
        )}
      </div>

      {/* Zone de saisie */}
      <div style={{ position: 'fixed', bottom: 72, left: 0, right: 0, padding: '12px 16px', background: 'rgba(240,242,255,0.95)', backdropFilter: 'blur(10px)', borderTop: '1px solid #E0E4F0' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            placeholder="Posez votre question à l'assistant..."
            rows={1}
            style={{ flex: 1, borderRadius: 20, border: '2px solid #E0E4F0', padding: '10px 16px', fontSize: 14, resize: 'none', outline: 'none', fontFamily: 'inherit', background: 'white', lineHeight: 1.5, maxHeight: 100, overflowY: 'auto' }}
          />
          <button
            onClick={() => sendMessage()}
            disabled={loading || !input.trim()}
            style={{ width: 44, height: 44, borderRadius: '50%', background: input.trim() ? 'linear-gradient(135deg, #6C63FF, #9C88FF)' : '#E0E4F0', border: 'none', cursor: input.trim() ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0, transition: 'background 0.2s' }}>
            {loading ? <span className="spinner" style={{ width: 16, height: 16, borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'white' }} /> : '➤'}
          </button>
        </div>
      </div>
    </div>
  );
}
